import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Organizations } from '../types';
import { Organization } from '@app/shared/types';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { get, post, put } from '../../../shared/api/apiService';

export interface UseOrganizationsOptions {
  pageSize?: number;
  page?: number;
}

export function useOrganizations({
  pageSize = 100,
  page = 0,
}: UseOrganizationsOptions = {}) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['organizations', { pageSize, page }],
    queryFn: async () => {
      const idToken = await getIdToken();
      return await get<Organizations>(
        `/organizations?pageSize=${pageSize}&page=${page}`,
        apiUrl,
        idToken,
      );
    },
    enabled: isSignedIn,
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useCreateOrganization() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newOrg: Organization) => {
      const idToken = await getIdToken();
      return await post<Organization>(
        '/organizations',
        newOrg,
        apiUrl,
        idToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useUpdateOrganization() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newOrg: Organization) => {
      const idToken = await getIdToken();
      return await put<Organization>(
        `/organizations/${newOrg.id}`,
        newOrg,
        apiUrl,
        idToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useOrganization(id: string) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['organization', id],
    queryFn: async () => {
      const idToken = await getIdToken();
      return await get<Organization>(`/organizations/${id}`, apiUrl, idToken);
    },
    enabled: isSignedIn,
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}
